import { Inject, Injectable, Injector, Optional } from '@angular/core';

import { ApdLoaderConfig, LOADER_CONFIG } from './loader.config';
import { PatternMatchingService } from '@app/core/pattern-matching.service';
import { ReplaySubject, firstValueFrom } from 'rxjs';
import { ReleaseNoteControllerService } from '@app/generated';
import { isReachableProbeResponse } from '@app/core/utils/probe-reachability.util';

export interface RequestIdent {
  id: number;
  time: number;
  url: string;
}

@Injectable()
export class LoaderService {
  public countStream: ReplaySubject<number> = new ReplaySubject(1);
  public isLoadingStream: ReplaySubject<boolean> = new ReplaySubject(1);
  public backendReachableStream: ReplaySubject<boolean> = new ReplaySubject(1);
  private requests = new Map();
  private backendReachable = true;
  private reachabilityProbe: Promise<boolean> | null = null;

  public get count() {
    return this.requests.size;
  }
  public get isLoading() {
    return this.requests.size > 0;
  }

  public get isBackendReachable() {
    return this.backendReachable;
  }

  constructor(
    @Optional()
    @Inject(LOADER_CONFIG)
    private loaderConfig: ApdLoaderConfig,
    private patternMatchingService: PatternMatchingService,
    private injector: Injector,
  ) {
    this.countStream.next(this.count);
    this.isLoadingStream.next(this.isLoading);
    this.backendReachableStream.next(this.backendReachable);
  }

  public addRequest(request: RequestIdent) {
    this.requests.set(request.id, request);
    this.countStream.next(this.count);
    if (this.count === 1) {
      this.isLoadingStream.next(true);
    }
  }
  public removeRequest(request: any) {
    const r = this.requests.get(request.id);
    if (r) {
      this.requests.delete(r.id);
      this.countStream.next(this.count);
      if (this.count === 0) {
        this.isLoadingStream.next(false);
      }
    }
  }

  public ignoreResource(url: string): boolean {
    if (!this.loaderConfig) {
      return false;
    }

    return this.patternMatchingService.matches(this.loaderConfig.ignorePatterns, url);
  }

  public setBackendReachable(reachable: boolean) {
    if (this.backendReachable === reachable) {
      return;
    }
    this.backendReachable = reachable;
    this.backendReachableStream.next(reachable);
  }

  /**
   * Called when an API request fails with a status that *may* indicate the backend
   * is unreachable. Instead of flipping the whole app offline on a single failure,
   * we confirm with one check-update probe and only mark the backend unreachable
   * if that probe also fails. Concurrent failures are coalesced into a single
   * in-flight probe.
   */
  public reportPossiblyUnreachable() {
    if (!this.backendReachable) {
      return;
    }
    void this.runReachabilityProbe();
  }

  /**
   * Confirms backend reachability using the same check-update probe that
   * reportPossiblyUnreachable() relies on, resolving with the result. If the backend
   * is already known to be unreachable, resolves false without probing. Concurrent
   * callers share the single in-flight probe.
   */
  public confirmBackendReachable(): Promise<boolean> {
    if (!this.backendReachable) {
      return Promise.resolve(false);
    }
    return this.runReachabilityProbe();
  }

  private runReachabilityProbe(): Promise<boolean> {
    if (this.reachabilityProbe) {
      return this.reachabilityProbe;
    }
    const releaseNoteController = this.injector.get(ReleaseNoteControllerService);
    this.reachabilityProbe = firstValueFrom(releaseNoteController.checkUpdate())
      .then(() => {
        this.setBackendReachable(true);
        return true;
      })
      .catch((error: unknown) => {
        if (this.isReachableProbeError(error)) {
          this.setBackendReachable(true);
          return true;
        }
        this.setBackendReachable(false);
        return false;
      })
      .finally(() => {
        this.reachabilityProbe = null;
      });
    return this.reachabilityProbe;
  }

  private isReachableProbeError(error: unknown): boolean {
    return isReachableProbeResponse(error);
  }
}
